import { useState } from 'react';
import { Image } from 'expo-image';
import { Link } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, maxContentWidth, radii, space, spacing, type } from '~/ui/tokens';
import { useCurrency } from '~/stores/useCurrency';

type FooterHref = '/' | '/#why-prices-are-lower' | '/search' | '/help' | '/terms' | '/privacy' | '/privacy#cookies';

const COLUMNS: { title: string; links: { label: string; href: FooterHref; disabled?: boolean }[] }[] = [
  {
    title: 'SuiteScanner',
    links: [
      { label: 'How it works', href: '/#why-prices-are-lower' },
      { label: 'Contact', href: '/help' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Centre', href: '/help' },
      { label: 'FAQs', href: '/help' },
      { label: 'Booking support', href: '/help' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Cookies', href: '/privacy#cookies' },
    ],
  },
  {
    title: 'Explore',
    links: [
      // Home is the hotels search/landing experience — there is no separate
      // "/search" listing page worth sending footer traffic to instead.
      { label: 'Hotels', href: '/' },
      // No flights product yet — disabled rather than linking anywhere, same
      // as the homepage's own Hotels/Flights/Cars mode pill.
      { label: 'Flights', href: '/', disabled: true },
    ],
  },
];

export function Footer() {
  // Just the selected code for display — rates are already kept fresh by
  // TopNav, which is present on every screen that also renders this footer.
  const currency = useCurrency((state) => state.selectedCurrency);

  return (
    <View style={styles.footer}>
      <View style={styles.inner}>
        <View style={styles.top}>
          <View style={styles.brandBlock}>
            {/* Brand logo (mark + wordmark), tinted white so it reads on the
                dark footer — replaces the plain "suitescanner" wordmark text. */}
            <View style={styles.logoViewport}>
              <Image
                source={require('../../assets/images/suitescanner_wordmark.png')}
                contentFit="contain"
                contentPosition="left"
                tintColor={colors.white}
                style={styles.logoImage}
                accessibilityLabel="SuiteScanner"
              />
            </View>
            <Text style={styles.tagline}>Every hotel has a story, find yours ❤️</Text>
            <View style={styles.partnerRow}>
              <Text style={styles.partnerLabel}>Powered by</Text>
              <Image source={require('../../assets/images/emerging-travel-group.png')} contentFit="contain" style={styles.partnerLogo} accessibilityLabel="Emerging Travel Group" />
              <Text style={styles.partnerName}>Emerging Travel Group (ETG)</Text>
            </View>
          </View>

          <View style={styles.columns}>
            {COLUMNS.map((column) => (
              <View key={column.title} style={styles.column}>
                <Text style={styles.columnTitle}>{column.title}</Text>
                {column.links.map((link) => (
                  <FooterLink key={link.label} label={link.label} href={link.href} disabled={link.disabled} />
                ))}
              </View>
            ))}
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.bottomRow}>
          <Text style={styles.copyright}>© SuiteScanner</Text>
          <View style={styles.bottomRight}>
            <View style={styles.localePill}>
              <Ionicons name="globe-outline" size={15} color={colors.lineSoft} />
              <Text style={styles.localeText}>{currency === 'EUR' ? 'EURO' : currency} · English</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

function FooterLink({ label, href, disabled }: { label: string; href: FooterHref; disabled?: boolean }) {
  const [hovered, setHovered] = useState(false);

  if (disabled) {
    return (
      <View style={styles.linkRow} accessibilityLabel={`${label}, coming soon`}>
        <Text style={styles.linkDisabled}>{label} <Text style={styles.linkSoon}>· Coming soon</Text></Text>
      </View>
    );
  }

  return (
    <Link href={href} asChild>
      <Pressable
        accessibilityRole="link"
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        style={styles.linkRow}
      >
        <Text style={[styles.link, hovered && styles.linkHovered]}>{label}</Text>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  footer: {
    backgroundColor: colors.ink,
    paddingTop: space.sectionMajor,
    paddingBottom: space.card,
  },
  inner: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: space.card,
  },
  top: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: space.cardLarge,
  },
  brandBlock: {
    flexGrow: 1,
    flexBasis: 200,
    minWidth: 160,
    gap: space.related,
  },
  // Fixed-size box for the wordmark PNG, same approach as the top nav so the two logos match.
  logoViewport: { width: 229, height: 40, overflow: 'hidden' },
  logoImage: { width: '100%', height: '100%' },
  tagline: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkFaint,
    marginTop: space.related,
  },
  partnerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: space.related,
    marginTop: space.compact,
  },
  partnerLabel: {
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  partnerLogo: {
    width: 30,
    height: 30,
    borderRadius: radii.sm,
  },
  partnerName: {
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    color: colors.white,
  },
  columns: {
    flexGrow: 3,
    flexBasis: 300,
    // minWidth:0 lets the block shrink to the phone width instead of forcing a
    // 460px row that overflows and gets clipped.
    minWidth: 0,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: space.card,
    rowGap: space.card,
  },
  column: {
    flexGrow: 1,
    flexBasis: 110,
    minWidth: 110,
    gap: space.compact,
  },
  columnTitle: {
    fontFamily: fontFamily.semibold,
    fontSize: type.bodyRegular.fontSize,
    letterSpacing: 0.3,
    color: colors.white,
    marginBottom: space.micro,
  },
  linkRow: {
    alignSelf: 'flex-start',
  },
  link: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkFaint,
  },
  linkHovered: {
    color: colors.white,
  },
  linkDisabled: {
    fontFamily: fontFamily.regular,
    ...type.bodyRegular,
    color: colors.inkFaint,
    opacity: 0.55,
  },
  linkSoon: {
    fontSize: type.metadata.fontSize,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.10)',
    marginTop: space.cardLarge,
    marginBottom: space.card,
  },
  bottomRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.internal,
  },
  copyright: {
    fontFamily: fontFamily.regular,
    fontSize: type.bodyRegular.fontSize,
    color: colors.inkFaint,
  },
  bottomRight: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: space.internal,
  },
  localePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    borderRadius: radii.full,
    paddingVertical: spacing(0.75),
    paddingHorizontal: spacing(1.25),
  },
  localeText: {
    fontFamily: fontFamily.medium,
    fontSize: type.bodyRegular.fontSize,
    color: colors.lineSoft,
  },
  socials: {
    flexDirection: 'row',
    gap: space.related,
  },
  socialButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.full,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  socialButtonPressed: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
});
